import React, { useRef, useState } from 'react';
import { useEffect } from 'react';

import classNames from 'classnames';
import { useIntl } from 'react-intl';
import { Form } from 'react-bootstrap';
import PlacesAutocomplete, { geocodeByAddress } from 'react-places-autocomplete';

import { useAppSelector, useGoogleMaps } from 'hooks';
import { addressFormatterFromGMaps, addressFormatterInline } from 'utils/Address';
import { getCountryCodeByGid } from 'store/groups/reducer';
import regex from 'constants/regex';

import MatchString from './MatchString';

interface IAddressAutocompleteProps {
  onChange: (address: IAddress) => any;
  disabled?: boolean;
  defaultAddress?: IAddress | null;
  defaultErrorStatus?: boolean;
  gid?: string;
  /**
   * Restricts suggestions to this ISO country, for callers with no group to derive it from. The
   * signup wizard is one: it runs unauthenticated, so the groups slice is empty and the gid lookup
   * yields nothing, which would leave Places suggesting addresses worldwide on a US-only form.
   */
  country?: string;
  isValid?: (status: boolean) => any;
  required?: boolean;
  short?: boolean;
  name?: string;
}

const AddressAutocomplete = ({
  onChange,
  disabled,
  defaultAddress,
  defaultErrorStatus,
  gid,
  country,
  isValid,
  required,
  short,
  name
}: IAddressAutocompleteProps) => {
  const intl = useIntl();
  const mapsStatus = useGoogleMaps();
  const seed = defaultAddress
    ? short
      ? defaultAddress.street_address_line_1
      : addressFormatterInline(defaultAddress)
    : '';
  const [address, setAddress] = useState<string>(seed);
  const addressRef = useRef(address);
  addressRef.current = address;
  const focused = useRef(false);
  const [selectedAddress, setSelectedAddress] = useState<IAddress | null>(null);
  const gidCountryCode = useAppSelector((state) => getCountryCodeByGid(state, gid));
  const countryCode = country ?? gidCountryCode;
  const [touched, setTouched] = useState<boolean>(false);
  const defautlError = {
    status: defaultErrorStatus || false,
    message: intl.formatMessage({ id: 'FieldNotEmptyKey' })
  };
  const [error, setError] = useState<{ status: boolean; message: string }>(defautlError);

  const searchOptions = {
    componentRestrictions: {
      country: countryCode
    }
  };

  /**
   * What the geocoder last resolved, in a ref as well as state.
   *
   * Picking a suggestion also blurs the field, and `geocodeByAddress` is a promise: blur therefore
   * ran while `selectedAddress` was still null, fell back to `defaultAddress`, and grafted the newly
   * typed street onto the *prefilled* city, state and postal code. On a destination-taxed order that
   * is a silently wrong tax rate, not a cosmetic glitch (Charles, 2026-09-14). A ref settles
   * synchronously, so blur sees the selection the render has not caught up with yet.
   */
  const selectedRef = useRef<IAddress | null>(null);
  /** A geocode in flight: blur must leave the field alone and let it answer. */
  const selectPending = useRef(false);

  /**
   * The street is the one address field this input owns rather than Formik, so a value that lands
   * in the form after mount has to be pulled in here too, or the box stays blank while the rest of
   * the address shows. A value the user is typing wins; a stale geocoder pick must not outlive it.
   */
  useEffect(() => {
    if (focused.current && addressRef.current !== '') return;
    selectedRef.current = null;
    setSelectedAddress(null);
    setAddress(seed);
  }, [seed]);

  const handleSelect = (address: any) => {
    if (address) {
      selectPending.current = true;
      geocodeByAddress(address)
        .then((results) => {
          const formattedAddress = addressFormatterFromGMaps(results[0]);
          if (formattedAddress) {
            setError({ ...defautlError, status: false });
            selectedRef.current = formattedAddress;
            onChange(formattedAddress);
            setSelectedAddress(formattedAddress);
            setAddress(short ? formattedAddress.street_address_line_1 : address);
          } else {
            setError({ status: true, message: intl.formatMessage({ id: 'wrongAddressKey' }) });
          }
        })
        .finally(() => {
          selectPending.current = false;
        });
    } else {
      required && setError({ ...defautlError, status: true });
    }
  };

  const handleChange = (address: string) => setAddress(address.replace(regex.emoji, ''));

  const handleFocus = () => {
    focused.current = true;
  };

  const handleBlur = () => {
    focused.current = false;
    if (short && defaultAddress) {
      // A pick already in flight owns the answer; overwriting it here is what produced the hybrid.
      if (selectPending.current) {
        return;
      }
      const baseAddress = selectedRef.current || selectedAddress || defaultAddress;
      // Typing a street under a base address from somewhere else is the same hybrid by hand, so the
      // rest of the address only travels with the street it was resolved for.
      if (baseAddress.street_address_line_1 !== address) {
        onChange({
          ...baseAddress,
          street_address_line_1: address,
          city: '',
          state_province: '',
          postal_code: ''
        });
        return;
      }
      onChange(baseAddress);
    } else {
      setTouched(true);
      handleSelect(address);
    }
  };

  useEffect(() => {
    isValid && isValid(!error.status);
  }, [error.status, isValid]);

  /**
   * Without Places there is no pick to carry a city or postal code, so only the street travels and
   * the rest of the address stays as typed.
   */
  const handleManualBlur = () => {
    focused.current = false;
    setTouched(true);
    if (required && !address) {
      setError({ ...defautlError, status: true });
      return;
    }
    setError({ ...defautlError, status: false });
    if (short && defaultAddress) {
      onChange({ ...defaultAddress, street_address_line_1: address });
    }
  };

  // PlacesAutocomplete throws while the Maps script is still on its way. Until it arrives the field
  // waits; if it never does, the street is typed by hand like any other address field.
  if (mapsStatus !== 'ready') {
    return (
      <div className="address-autocomplete position-relative">
        <Form.Control
          name={name || undefined}
          value={address}
          placeholder={intl.formatMessage({ id: 'searchAddressKey' })}
          isInvalid={touched && error.status}
          disabled={disabled || mapsStatus === 'loading'}
          onChange={(event) => handleChange(event.target.value)}
          onFocus={handleFocus}
          onBlur={handleManualBlur}
        />
        {error.status && touched && (
          <Form.Control.Feedback type="invalid">{error.message}</Form.Control.Feedback>
        )}
      </div>
    );
  }

  return (
    <PlacesAutocomplete
      value={address}
      onChange={handleChange}
      onSelect={handleSelect}
      searchOptions={searchOptions}
      debounce={1000}
    >
      {({ getInputProps, suggestions, getSuggestionItemProps }) => (
        <div className="address-autocomplete position-relative">
          <Form.Control
            isInvalid={touched && error.status}
            {...getInputProps({
              name: name || undefined,
              placeholder: intl.formatMessage({ id: 'searchAddressKey' }),
              onFocus: handleFocus,
              onBlur: handleBlur,
              disabled
            })}
          />
          {error.status && touched && (
            <Form.Control.Feedback type="invalid">{error.message}</Form.Control.Feedback>
          )}
          {suggestions.length > 0 && (
            <div className="suggestions" data-test="address-autocomplete">
              {suggestions.map((suggestion, index) => {
                const className = classNames('item', {
                  active: suggestion.active
                });
                const props = getSuggestionItemProps(suggestion, {
                  className
                });
                props.key = index;
                /* eslint-disable react/jsx-key */
                return (
                  <div {...props} data-test={`suggestion-${index}`}>
                    <MatchString
                      matchedSubstrings={suggestion.matchedSubstrings[0]}
                      mainText={suggestion.formattedSuggestion.mainText}
                    />
                    <small>{suggestion.formattedSuggestion.secondaryText}</small>
                  </div>
                );
                /* eslint-enable react/jsx-key */
              })}
            </div>
          )}
        </div>
      )}
    </PlacesAutocomplete>
  );
};

export default AddressAutocomplete;
